import { useEffect, useRef, useState, useCallback } from 'react'
import { Video, VideoOff, Mic, MicOff, PhoneOff, Users, Settings, Monitor, MonitorOff } from 'lucide-react'
import { motion } from 'framer-motion'
import { useFamily } from '../hooks/useFamily'

// Demo: apenas pré-visualização local da câmera e microfone, sem sinalização
const VideoRoom = () => {
  const { familyMembers } = useFamily()
  const localVideoRef = useRef(null)
  const [stream, setStream] = useState(null)
  const [videoOn, setVideoOn] = useState(false)
  const [audioOn, setAudioOn] = useState(false)
  const [error, setError] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [connectionStatus, setConnectionStatus] = useState('disconnected')
  const [participants, setParticipants] = useState([])
  const [showSettings, setShowSettings] = useState(false)
  const [videoQuality, setVideoQuality] = useState('auto')

  const startCamera = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)
      
      const constraints = {
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          frameRate: { ideal: 30 }
        },
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      }

      const media = await navigator.mediaDevices.getUserMedia(constraints)
      setStream(media)
      
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = media
      }
      
      setVideoOn(true)
      setAudioOn(true)
      setConnectionStatus('connected')
      
      // Simular participantes da família (demo)
      setParticipants(familyMembers.slice(0, 3))
      
    } catch (e) {
      setError(`Erro ao acessar câmera/microfone: ${e.message}`)
      setConnectionStatus('error')
    } finally {
      setIsLoading(false)
    }
  }, [familyMembers])

  const stopCamera = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach(t => t.stop())
      setStream(null)
      setVideoOn(false)
      setAudioOn(false)
      setConnectionStatus('disconnected')
      setParticipants([])
    }
  }, [stream])

  const toggleVideo = useCallback(() => {
    if (!stream) return
    const vt = stream.getVideoTracks()[0]
    if (vt) {
      vt.enabled = !vt.enabled
      setVideoOn(vt.enabled)
    }
  }, [stream])

  const toggleAudio = useCallback(() => {
    if (!stream) return
    const at = stream.getAudioTracks()[0]
    if (at) {
      at.enabled = !at.enabled
      setAudioOn(at.enabled)
    }
  }, [stream])

  const getConnectionStatusColor = () => {
    switch (connectionStatus) {
      case 'connected': return 'text-green-600'
      case 'connecting': return 'text-yellow-600'
      case 'error': return 'text-red-600'
      default: return 'text-gray-600'
    }
  }

  const getConnectionStatusText = () => {
    switch (connectionStatus) {
      case 'connected': return 'Conectado'
      case 'connecting': return 'Conectando...'
      case 'error': return 'Erro de conexão'
      default: return 'Desconectado'
    }
  }

  useEffect(() => {
    return () => stopCamera()
  }, [stopCamera])

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Sala de Vídeo da Família</h1>
          <p className="text-neutral-600 mt-1">
            Status: <span className={getConnectionStatusColor()}>{getConnectionStatusText()}</span>
            {participants.length > 0 && ` • ${participants.length + 1} participantes`}
          </p>
        </div>
        <div className="flex gap-2">
          {!stream ? (
            <button 
              onClick={startCamera} 
              disabled={isLoading}
              className="btn-primary flex items-center gap-2 disabled:opacity-50"
            >
              <Video className="h-4 w-4" /> 
              {isLoading ? 'Conectando...' : 'Iniciar chamada'}
            </button>
          ) : (
            <button onClick={stopCamera} className="btn-secondary flex items-center gap-2">
              <PhoneOff className="h-4 w-4" /> Encerrar chamada
            </button>
          )}
          <button 
            onClick={() => setShowSettings(!showSettings)}
            className="btn-secondary flex items-center gap-2"
          >
            <Settings className="h-4 w-4" />
          </button>
        </div>
      </div>

      {error && (
        <div className="p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>
      )}

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-sm border border-neutral-200 p-4"
      >
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* Vídeo Principal */}
          <div className="lg:col-span-3">
            <div className="aspect-video bg-neutral-100 rounded-lg overflow-hidden flex items-center justify-center relative max-w-2xl mx-auto">
              {stream ? (
                <video 
                  ref={localVideoRef} 
                  autoPlay 
                  playsInline 
                  muted 
                  className="w-full h-full object-cover" 
                />
              ) : (
                <div className="text-center space-y-2">
                  <Video className="h-12 w-12 mx-auto text-neutral-400" />
                  <div className="text-neutral-500">Câmera desligada</div>
                  <div className="text-sm text-neutral-400">Clique em "Iniciar chamada" para começar</div>
                </div>
              )}
              
              {/* Overlay de status */}
              {stream && (
                <div className="absolute top-4 left-4 bg-black bg-opacity-50 text-white px-2 py-1 rounded text-sm">
                  Você
                </div>
              )}
            </div>

            {/* Controles */}
            <div className="flex items-center justify-center gap-2 mt-3">
              <button 
                onClick={toggleVideo} 
                disabled={!stream} 
                className={`px-3 py-2 text-sm rounded-lg border flex items-center gap-1 disabled:opacity-50 ${
                  !videoOn ? 'bg-red-100 text-red-700 border-red-200 hover:bg-red-200' : 'bg-white text-neutral-700 border-neutral-300 hover:bg-neutral-50'
                }`}
              >
                {videoOn ? <Video className="h-3 w-3" /> : <VideoOff className="h-3 w-3" />} 
                Vídeo
              </button>
              <button 
                onClick={toggleAudio} 
                disabled={!stream} 
                className={`px-3 py-2 text-sm rounded-lg border flex items-center gap-1 disabled:opacity-50 ${
                  !audioOn ? 'bg-red-100 text-red-700 border-red-200 hover:bg-red-200' : 'bg-white text-neutral-700 border-neutral-300 hover:bg-neutral-50'
                }`}
              >
                {audioOn ? <Mic className="h-3 w-3" /> : <MicOff className="h-3 w-3" />} 
                Áudio
              </button>
            </div>
          </div>

          {/* Lista de Participantes */}
          <div className="space-y-2">
            <h3 className="font-medium text-neutral-900 flex items-center gap-2 text-sm">
              <Users className="h-4 w-4 text-primary-500" />
              Participantes ({participants.length + 1})
            </h3>
            
            {/* Você */}
            <div className="p-2 rounded-lg bg-primary-50 border border-primary-200">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 bg-primary-500 rounded-full flex items-center justify-center text-white text-xs font-medium">
                  EU
                </div>
                <div>
                  <div className="font-medium text-neutral-900 text-sm">Você</div>
                  <div className="text-xs text-neutral-500">
                    {videoOn ? '📹' : '📹❌'} {audioOn ? '🎤' : '🎤❌'}
                  </div>
                </div>
              </div>
            </div>

            {/* Outros participantes (demo) */}
            {participants.map((member, index) => (
              <div key={member.id || index} className="p-2 rounded-lg bg-neutral-50 border border-neutral-200">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 bg-neutral-400 rounded-full flex items-center justify-center text-white text-xs font-medium">
                    {member.full_name?.charAt(0) || member.name?.charAt(0) || '?'}
                  </div>
                  <div>
                    <div className="font-medium text-neutral-900 text-sm">
                      {member.full_name || member.name || 'Membro'}
                    </div>
                    <div className="text-xs text-neutral-500">
                      📹 🎤 (demo)
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {participants.length === 0 && (
              <div className="text-center py-3 text-neutral-500">
                <Users className="h-6 w-6 mx-auto mb-1 text-neutral-400" />
                <div className="text-xs">Aguardando membros...</div>
              </div>
            )}
          </div>
        </div>

        {/* Configurações */}
        {showSettings && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="mt-4 p-3 bg-neutral-50 rounded-lg border border-neutral-200"
          >
            <h4 className="font-medium text-neutral-900 mb-2 text-sm">Configurações</h4>
            <div className="space-y-2">
              <div>
                <label className="block text-xs font-medium text-neutral-700 mb-1">
                  Qualidade do Vídeo
                </label>
                <select 
                  value={videoQuality} 
                  onChange={(e) => setVideoQuality(e.target.value)}
                  className="w-full px-2 py-1 text-sm border border-neutral-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="auto">Automática</option>
                  <option value="720p">720p HD</option>
                  <option value="480p">480p</option>
                  <option value="360p">360p</option>
                </select>
              </div>
              <div className="text-xs text-neutral-500">
                💡 Para chamadas reais, será necessário implementar sinalização via WebRTC.
              </div>
            </div>
          </motion.div>
        )}
      </motion.div>
    </div>
  )
}

export default VideoRoom
